import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";

const DataCacheContext = createContext(null);

// cache configuration
const CACHE_VERSION = 1;
const CACHE_PREFIX = "vrtta_cache";
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

// cache keys for different data types
export const CACHE_KEYS = {
  DONATE_MATERIALS: "donateMaterials",
  DONATE_MATERIALS_WITH_RECEIVED_DATE: "donateMaterialsWithReceivedDate",
  BROWSE_MATERIALS: "browseMaterials",
  ALLOCATE_LISTINGS: "allocateListings",
  DONOR_INVENTORY: "donorInventory",
  DONOR_GHG: "donorGhg",
  RECEIVER_INVENTORY: "receiverInventory",
  RECEIVER_MFCA: "receiverMfca",
  RECEIVER_GHG: "receiverGhg",
  RECEIVER_SCHEDULE: "receiverSchedule",
  PROFILE_GHG_GOALS: "profileGhgGoals",
  ADMIN_USERS: "adminUsers",
  ADMIN_ORGANIZATIONS: "adminOrganizations",
  ADMIN_PLATFORM_ACTIVITY: "adminPlatformActivity",
  ADMIN_MATERIALS: "adminMaterials",
  ADMIN_PARTNERSHIPS: "adminPartnerships",
  RECEIVER_APPROVED_REQUESTS: "receiverApprovedRequests",
};

// get the user ID from localStorage
const getUserId = () => {
  try {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      const user = JSON.parse(userStr);
      return user.id || user.user_id || "anonymous";
    }
  } catch (e) {
    console.warn("Could not get user ID for cache:", e);
  }
  return "anonymous";
};

const buildStorageKey = (cacheKey) => {
  const userId = getUserId();
  return `${CACHE_PREFIX}_v${CACHE_VERSION}_${userId}_${cacheKey}`;
};

const readFromStorage = (cacheKey) => {
  try {
    const storageKey = buildStorageKey(cacheKey);
    const stored = localStorage.getItem(storageKey);
    if (!stored) return null;

    const parsed = JSON.parse(stored);
    
    // check if cache is expired (older than 7 days)
    if (parsed.timestamp && Date.now() - parsed.timestamp > MAX_AGE_MS) {
      console.log(`Cache expired for ${cacheKey}, clearing...`);
      localStorage.removeItem(storageKey);
      return null;
    }

    return parsed;
  } catch (e) {
    console.warn(`Could not read cache for ${cacheKey}:`, e);
    return null;
  }
};

const writeToStorage = (cacheKey, data, timestamp) => {
  try {
    const storageKey = buildStorageKey(cacheKey);
    const cacheEntry = { data, timestamp, version: CACHE_VERSION };
    
    // check size before storing
    const serialized = JSON.stringify(cacheEntry);
    if (serialized.length > 500000) { // 500KB limit per entry
      console.warn(`Cache entry too large for ${cacheKey}, skipping localStorage`);
      return false;
    }
    
    localStorage.setItem(storageKey, serialized);
    return true;
  } catch (e) {
    console.warn(`Could not write cache for ${cacheKey}:`, e);
    if (e.name === "QuotaExceededError") {
      console.warn("localStorage quota exceeded, clearing old cache entries");
      clearOldCacheEntries();
    }
    return false;
  }
};

const clearFromStorage = (cacheKey) => {
  try {
    const storageKey = buildStorageKey(cacheKey);
    localStorage.removeItem(storageKey);
  } catch (e) {
    console.warn(`Could not clear cache for ${cacheKey}:`, e);
  }
};

const clearOldCacheEntries = () => {
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        if (!key.includes(`_v${CACHE_VERSION}_`)) {
          keysToRemove.push(key);
        }
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (e) {
    console.warn("Could not clear old cache entries:", e);
  }
};

export function DataCacheProvider({ children }) {
  // use ref instead of state to avoid re-renders when cache is populated
  const cacheRef = useRef({});
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    // only clear stale version entries on mount — don't eagerly load all keys
    clearOldCacheEntries();
    setInitialized(true);
  }, []);

  // lazy: check in-memory ref first, then localStorage on demand
  const getCachedData = useCallback((key) => {
    if (cacheRef.current[key]) {
      return cacheRef.current[key];
    }
    // lazy-load from localStorage and promote to in-memory cache
    const stored = readFromStorage(key);
    if (stored) {
      cacheRef.current[key] = stored;
    }
    return stored;
  }, []);

  const setCachedData = useCallback((key, data, timestamp = Date.now()) => {
    const cacheEntry = { data, timestamp };
    cacheRef.current[key] = cacheEntry;
    writeToStorage(key, data, timestamp);
  }, []);

  const clearCache = useCallback((key) => {
    delete cacheRef.current[key];
    clearFromStorage(key);
  }, []);

  const clearAllCache = useCallback(() => {
    cacheRef.current = {};
    Object.values(CACHE_KEYS).forEach(key => clearFromStorage(key));
  }, []);

  // measure in-memory cache size for dev monitoring
  const getCacheMemoryStats = useCallback(() => {
    const entries = [];
    let totalBytes = 0;

    for (const [key, value] of Object.entries(cacheRef.current)) {
      try {
        const serialized = JSON.stringify(value);
        const size = serialized.length * 2; // UTF-16 estimate
        entries.push({ name: key, size });
        totalBytes += size;
      } catch (_) {
        entries.push({ name: key, size: 0 });
      }
    }

    return { totalBytes, entryCount: entries.length, entries };
  }, []);

  return (
    <DataCacheContext.Provider
      value={{
        getCachedData,
        setCachedData,
        clearCache,
        clearAllCache,
        getCacheMemoryStats,
        initialized,
      }}
    >
      {children}
    </DataCacheContext.Provider>
  );
}

export function useDataCache() {
  const context = useContext(DataCacheContext);
  if (!context) {
    throw new Error("useDataCache must be used within a DataCacheProvider");
  }
  return context;
}

export default DataCacheContext;
